import { useField, useFormikContext } from 'formik';
import Button from '../../../../button/Button';
import FormTextField from '../../../../form/FormTextField';
import { RadioButtons } from '../../../../radiobuttons/radiobuttons';
import { BeneficialPersonalOwner, OwnerTreeStructure } from '../../../schema';
import { INDIVIDUAL_OWNERS_FORM_STRING } from '../OwnersTree';
import { useEffect, useState } from 'react';
import {
    FormSingleSelectField,
    WrappedFormSingleSelectField,
} from '../../../../form/FormSingleSelectField';
import { beneficialOwnersStructureSchema } from '../../../validation';
import { getDirectOwnersPercentageTotal } from '../helpers';
import { useVerificationContext } from '../../verificationContext';

type NewIndividualFormProps = {
    hideLeftBar: boolean;
    fieldname: string;
    parentFieldname: string;
    ownerGuid: string;
    handleDeleteOwner: () => void;
};
export const NewIndividualForm: React.FC<NewIndividualFormProps> = ({
    ownerGuid,
    fieldname,
    parentFieldname,
    hideLeftBar,
    handleDeleteOwner,
}) => {
    const [{ value: parentValue }] = useField<OwnerTreeStructure | OwnerTreeStructure[]>(
        parentFieldname
    );
    const [{ value }, , { setValue: setIndividuals }] = useField<BeneficialPersonalOwner[] | null>(
        INDIVIDUAL_OWNERS_FORM_STRING
    );
    const [, , { setValue: setTreeIsNewValue }] = useField<boolean | undefined>(
        `${fieldname}.isNewNode`
    );
    const [{ value: ownerStartingGuid }] = useField<string>(`${fieldname}.ownersGuid`);
    const [{ value: treeValue }, , { setTouched }] = useField<OwnerTreeStructure>(fieldname);
    const { setFieldTouched } = useFormikContext();
    const { selectOptions } = useVerificationContext();

    // New nodes are always inserted/appended at an array index that a previously
    // cancelled add may have already left touched, which would otherwise show that
    // stale item's validation errors immediately on this brand new, untouched form.
    useEffect(() => {
        setFieldTouched(`${fieldname}.positionAtCompany`, false, false);
        setFieldTouched(`${fieldname}.bControllingParty`, false, false);
        setFieldTouched(`${fieldname}.percentageOwned`, false, false);
        const index = (value ?? []).findIndex((o) => o.guid === ownerGuid);
        if (index >= 0) {
            setFieldTouched(`${INDIVIDUAL_OWNERS_FORM_STRING}[${index}].firstName`, false, false);
            setFieldTouched(`${INDIVIDUAL_OWNERS_FORM_STRING}[${index}].lastName`, false, false);
            setFieldTouched(
                `${INDIVIDUAL_OWNERS_FORM_STRING}[${index}].occupationTypesId`,
                false,
                false
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSetNewItemToFalse = () => {
        setTreeIsNewValue(false);
        /* We always add new entires to the start of the array but this element
           Will have been marked as touched when we add something new we should mark it 
           this index as not touched when we're done adding to avoid showing errors
           on the inputs of a new element */
        setTouched(false);
    };

    const [originalGuid] = useState(ownerStartingGuid);
    const removeOriginalOwner = () => {
        // Remove originally added owner
        // Guid may have changed from dropdown comp hence storing originalGuid
        setIndividuals((value ?? []).filter((item) => item.guid !== originalGuid));
    };
    const handleAddExistingOwner = () => {
        removeOriginalOwner();
        handleSetNewItemToFalse();
    };
    const handleDeleteOwnerExisiting = () => {
        removeOriginalOwner();
        handleDeleteOwner();
    };

    const siblingGuids = (Array.isArray(parentValue) ? parentValue : parentValue.children ?? [])
        .filter((o) => !o.bIsBusiness)
        .map((o) => o.ownersGuid);
    const options = (value ?? [])
        // Filter out existing siblings to not allwow repeats in the same branch level
        .filter((o) => !siblingGuids.includes(o.guid))
        .map((opt) => ({
            label: `${opt.firstName} ${opt.lastName}`,
            value: opt.guid,
        }));
    const hasExistingOwners = options.length > 0;
    const [ownerMethod, setOwnerMethod] = useState<'Existing' | 'New' | null>(
        !hasExistingOwners ? 'New' : null
    );

    const siblings = Array.isArray(parentValue) ? parentValue : (parentValue.children ?? []);
    const isControllingParty = treeValue.bControllingParty === true;
    const siblingTotal = getDirectOwnersPercentageTotal(siblings);
    // If this field's own value already exceeds 100, that's already flagged by its own
    // max(100) validation — don't also show the redundant aggregate-total message.
    const ownValueExceeds100 = (treeValue?.percentageOwned ?? 0) > 100;
    // Only controlling parties are allowed 0% — regular beneficial owners must be > 0%.
    const ownValueIsZeroOrLess =
        !isControllingParty &&
        treeValue?.percentageOwned != null &&
        treeValue.percentageOwned <= 0;
    const exceedsTotalPercentage = siblingTotal > 100 && !ownValueExceeds100;
    const exceedsTotalPercentageMessage =
        'The combined ownership percentage of beneficial owners cannot exceed 100%';

    const selectedOwnerIndex = (value ?? []).findIndex((o) => o.guid === ownerGuid)!;
    const selectedIndividual = selectedOwnerIndex >= 0 ? (value ?? [])[selectedOwnerIndex] : null;
    const individualNameValid =
        ownerMethod !== 'New' ||
        ((selectedIndividual?.firstName ?? '').trim().length > 0 &&
            (selectedIndividual?.lastName ?? '').trim().length > 0);

    const isOwnerStructureValid =
        beneficialOwnersStructureSchema.isValidSync(treeValue) &&
        !exceedsTotalPercentage &&
        individualNameValid &&
        (ownerMethod === 'Existing' ? treeValue.ownersGuid !== originalGuid : true);

    return (
        <div className="NewIndividualForm">
            {hideLeftBar && <div aria-hidden="true" className="FloatingHide Full" />}
            {!ownerMethod && (
                <div className="NewIndividualFormRow">
                    <Button
                        onClick={() => setOwnerMethod('New')}
                        type="button"
                        priority="secondary"
                    >
                        Add new individual
                    </Button>
                    <Button
                        onClick={() => setOwnerMethod('Existing')}
                        type="button"
                        priority="secondary"
                    >
                        Add existing individual
                    </Button>
                </div>
            )}
            {ownerMethod === 'New' && (
                <>
                    <div className="NewIndividualFormRow">
                        <FormTextField
                            label="First Name*"
                            field={`${INDIVIDUAL_OWNERS_FORM_STRING}[${selectedOwnerIndex}].firstName`}
                        />
                        <FormTextField
                            label="Last Name*"
                            field={`${INDIVIDUAL_OWNERS_FORM_STRING}[${selectedOwnerIndex}].lastName`}
                        />
                    </div>
                    <WrappedFormSingleSelectField
                        fieldName={`${INDIVIDUAL_OWNERS_FORM_STRING}[${selectedOwnerIndex}].occupationTypesId`}
                        options={selectOptions?.occupations ?? []}
                        label="Occupation"
                    />
                    <FormTextField
                        field={`${fieldname}.positionAtCompany`}
                        label={'Position at the Company*'}
                        maxLength={200}
                    />
                    <RadioButtons
                        options={['Yes', 'No']}
                        label={'Controlling Party*'}
                        booleanField={true}
                        fieldname={`${fieldname}.bControllingParty`}
                    />
                    <div className="FloatingErrorField">
                        <FormTextField
                            type="number"
                            label="Percentage of Shares Owned (%)*"
                            field={`${fieldname}.percentageOwned`}
                            hideError
                            className={
                                exceedsTotalPercentage || ownValueExceeds100 || ownValueIsZeroOrLess
                                    ? 'ErrorInput'
                                    : ''
                            }
                        />
                        {ownValueExceeds100 && (
                            <div className="ErrorLabel">
                                Percentage of shares owned cannot exceed 100%
                            </div>
                        )}
                        {ownValueIsZeroOrLess && !ownValueExceeds100 && (
                            <div className="ErrorLabel">
                                Percentage of shares owned must be greater than 0%
                            </div>
                        )}
                        {!ownValueExceeds100 && !ownValueIsZeroOrLess && exceedsTotalPercentage && (
                            <div className="ErrorLabel">{exceedsTotalPercentageMessage}</div>
                        )}
                    </div>
                    <div className="NewIndividualFormRow AddSave">
                        <Button onClick={handleDeleteOwner} type="button" priority="secondary">
                            Cancel
                        </Button>
                        <Button
                            disabled={!isOwnerStructureValid}
                            onClick={handleSetNewItemToFalse}
                            type="button"
                        >
                            Add
                        </Button>
                    </div>
                </>
            )}
            {ownerMethod === 'Existing' && (
                <>
                    <FormSingleSelectField<any>
                        options={options}
                        label="Choose from existing individuals"
                        fieldName={`${fieldname}.ownersGuid`}
                    />
                    <FormTextField
                        field={`${fieldname}.positionAtCompany`}
                        label={'Position at the Company*'}
                        maxLength={200}
                    />
                    <RadioButtons
                        options={['Yes', 'No']}
                        label={'Controlling Party*'}
                        booleanField={true}
                        fieldname={`${fieldname}.bControllingParty`}
                    />
                    <div className="FloatingErrorField">
                        <FormTextField
                            type="number"
                            label="Percentage of Shares Owned (%)*"
                            field={`${fieldname}.percentageOwned`}
                            hideError
                            className={
                                exceedsTotalPercentage || ownValueExceeds100 || ownValueIsZeroOrLess
                                    ? 'ErrorInput'
                                    : ''
                            }
                        />
                        {ownValueExceeds100 && (
                            <div className="ErrorLabel">
                                Percentage of shares owned cannot exceed 100%
                            </div>
                        )}
                        {ownValueIsZeroOrLess && !ownValueExceeds100 && (
                            <div className="ErrorLabel">
                                Percentage of shares owned must be greater than 0%
                            </div>
                        )}
                        {!ownValueExceeds100 && !ownValueIsZeroOrLess && exceedsTotalPercentage && (
                            <div className="ErrorLabel">{exceedsTotalPercentageMessage}</div>
                        )}
                    </div>
                    <div className="NewIndividualFormRow AddSave">
                        <Button
                            onClick={handleDeleteOwnerExisiting}
                            type="button"
                            priority="secondary"
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={!isOwnerStructureValid}
                            onClick={handleAddExistingOwner}
                            type="button"
                        >
                            Add
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};
